import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { useContest, ContestTheme, TermsVariables } from "@/hooks/useContest";
import { DEFAULT_ENTRY_METHODS_HEADING } from "@/lib/entryFields";
import { visibleEntryMethods } from "@/lib/entryMethods";
import { useEntry } from "@/hooks/useEntry";
import { ContestHeader } from "@/components/contest/ContestHeader";
import { EntryButton } from "@/components/contest/EntryButton";
import { ShareSection } from "@/components/contest/ShareSection";
import { EmailCollector } from "@/components/contest/EmailCollector";
import { ContestFooter } from "@/components/contest/ContestFooter";
import { WinnerNotificationInfo } from "@/components/contest/WinnerNotificationInfo";
import { Loader2, AlertCircle, Gift, StopCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { isPast } from "date-fns";
import { useOrgPlanPublic } from "@/hooks/useOrgPlanPublic";
import { TurnstileWidget, TurnstileWidgetRef } from "@/components/TurnstileWidget";
import { supabasePublic } from "@/integrations/supabase/publicClient";


export default function ContestPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");
  const captureSource = searchParams.get("src");
  const { toast } = useToast();

  const { data, isLoading, error } = useContest(slug || "");
  const entry = useEntry(slug || "", refCode, captureSource);

  // Turnstile State & Refs
  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  const [turnstileToken, setTurnstileToken] = useState<{ value: string; issuedAt: number } | null>(null);
  const [isWaitingForToken, setIsWaitingForToken] = useState(false);
  const tokenResolverRef = useRef<((token: string) => void) | null>(null);


  // Sticky Bar Logic
  const entryMethodsRef = useRef<HTMLDivElement>(null);
  const emailFormRef = useRef<HTMLDivElement>(null);
  const [scrollAfterSubmit, setScrollAfterSubmit] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // If entry methods (or email form) are visible, hide sticky bar
        setShowStickyBar(!entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    // Observe whichever section is relevant based on state
    if (!entry.email && emailFormRef.current) {
      observer.observe(emailFormRef.current);
    } else if (entry.email && entryMethodsRef.current) {
      observer.observe(entryMethodsRef.current);
    }

    return () => observer.disconnect();
  }, [entry.email]); // Re-run when auth state changes

  // Content stats now handled directly in useContest query

  // Fetch plan key
  const contestOrgId = data?.contest?.organization_id;
  const { planKey } = useOrgPlanPublic(contestOrgId);

  // Load participant
  useEffect(() => {
    if (slug) {
      const savedEmail = localStorage.getItem(`contest_email_${slug}`);
      if (savedEmail && !entry.email) {
        entry.setEmail(savedEmail);
        entry.loadParticipant(savedEmail).catch(() => {
          // Silent catch
        });
      }
    }
  }, [slug]);

  // Prewarm Turnstile Logic
  const prewarmTurnstile = useCallback(() => {
    // Skip if we already have a fresh token (< 45s)
    if (turnstileToken && (Date.now() - turnstileToken.issuedAt < 45000)) {
      return;
    }

    const status = turnstileRef.current?.getStatus();
    if (!status?.executing) {
      turnstileRef.current?.execute();
    }
  }, [turnstileToken]);

  useEffect(() => {
    if (!scrollAfterSubmit || !entry.email || entry.isLoadingParticipant) return;
    const frame = requestAnimationFrame(() => {
      const heading = document.getElementById("entered-to-win");
      (heading ?? entryMethodsRef.current)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setScrollAfterSubmit(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollAfterSubmit, entry.email, entry.isLoadingParticipant, entry.totalEntries]);

  // Prewarm when email is present and not loading
  useEffect(() => {
    if (entry.email && !entry.isLoadingParticipant && !entry.isLoading) {
      // Delay slightly to allow render stability
      const timer = setTimeout(() => {
        prewarmTurnstile();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [entry.email, entry.isLoadingParticipant, entry.isLoading, prewarmTurnstile]);

  const handleTokenReceived = (token: string) => {
    setTurnstileToken({ value: token, issuedAt: Date.now() });

    if (tokenResolverRef.current) {
      tokenResolverRef.current(token);
      tokenResolverRef.current = null;
      setIsWaitingForToken(false);
    }
  };

  const handleEmailSubmit = async (email: string, fieldsPayload: Record<string, any>) => {
    try {
      setScrollAfterSubmit(true);
      const result = await entry.loadParticipant(email, true, fieldsPayload);
      if (result.exists && result.id) {
        localStorage.setItem(`contest_email_${slug}`, email);
      }
    } catch (err: any) {
      setScrollAfterSubmit(false);
      if (err.message) {
        // If it's a specific validation error, bubble it up to EmailCollector
        if (err.message.includes("Missing required field") || err.message.includes("Required field")) {
          throw err;
        }
      }
      toast({
        title: "Registration Failed",
        description: err.message || "Please try again.",
        variant: "destructive"
      });
      throw err;
    }
  };

  const handleEntry = async (methodKey: string, methodId: string) => {
    // 1. Get Token (Prewarmed or New)
    let token = turnstileToken?.value;

    // Check freshness <= 45s
    if (token && turnstileToken && (Date.now() - turnstileToken.issuedAt > 45000)) {
      token = undefined;
      setTurnstileToken(null);
    }

    if (!token) {
      setIsWaitingForToken(true);

      // Reset before execute to ensure clean state
      turnstileRef.current?.reset();

      const status = turnstileRef.current?.getStatus();
      if (!status?.executing) {
        turnstileRef.current?.execute();
      }

      // Wait for token
      try {
        token = await new Promise<string>((resolve, reject) => {
          tokenResolverRef.current = resolve;
          // Fallback timeout for UI safety (25s)
          setTimeout(() => {
            if (tokenResolverRef.current === resolve) {
              tokenResolverRef.current = null;
              reject(new Error("Verification Timeout"));
            }
          }, 25000);
        });
      } catch (e) {
        setIsWaitingForToken(false);
        toast({
          title: "Quick verification failed—please try again.",
          description: "The security check timed out.",
          variant: "destructive"
        });
        return;
      }
    }

    // Consume token immediately
    setTurnstileToken(null);

    // 2. Submit Entry
    let result = await entry.submitEntry(methodKey, methodId, token);

    // 3. Auto-Retry Logic (if Bot Check Failed)
    if (!result.ok && (result.code === 'BOT_CHECK_FAILED' || result.error?.includes("verification") || result.error?.includes("Turnstile"))) {
      // Reset and get new token
      turnstileRef.current?.reset();
      setIsWaitingForToken(true);
      turnstileRef.current?.execute();

      try {
        // Wait for new token
        const newToken = await new Promise<string>((resolve) => {
          tokenResolverRef.current = resolve;
        });

        // Retry submission
        result = await entry.submitEntry(methodKey, methodId, newToken);
      } catch (e) {
        // Retry token fetch failed
      }
      setIsWaitingForToken(false);
    } else {
      setIsWaitingForToken(false);
    }

    // 4. Handle Final Result
    if (result.ok && result.awardedEntries > 0) {
      toast({
        title: "🎉 Entry Submitted!",
        description: `You earned ${result.awardedEntries} ${result.awardedEntries === 1 ? "entry" : "entries"}. Total: ${result.totalEntriesForParticipant}`,
      });
      // Prewarm for next action
      setTimeout(prewarmTurnstile, 1000);
    } else if (result.alreadyCompleted) {
      toast({
        title: "Already Completed",
        description: "You've already completed this action.",
      });
      setTimeout(prewarmTurnstile, 1000);
    } else if (result.error) {
      if (result.code === 'BOT_CHECK_FAILED') {
        toast({
          title: "Verification Failed",
          description: "We couldn’t verify this request. Please refresh and try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Entry Failed",
          description: result.error,
          variant: "destructive",
        });
      }
      setTimeout(prewarmTurnstile, 1000);
    }

    return result;
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading contest...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Contest Not Found</h1>
          <p className="text-muted-foreground">
            This contest may have ended or the link is incorrect.
          </p>
        </div>
      </div>
    );
  }

  const { contest, entryMethods: loadedEntryMethods } = data;
  const entryMethods = visibleEntryMethods(loadedEntryMethods);
  const isManuallyEnded = !!contest.ended_at;
  const isExpired = contest.ends_at && isPast(new Date(contest.ends_at));
  const hasStarted = !contest.starts_at || isPast(new Date(contest.starts_at));
  const isEntryDisabled = isManuallyEnded || isExpired || !hasStarted;

  const theme = contest.theme_json as ContestTheme | null;
  const termsVariables = (contest.terms_variables_json || {}) as TermsVariables;
  const backgroundStyle = theme?.background
    ? { backgroundColor: theme.background, '--contest-bg': theme.background } as React.CSSProperties
    : undefined;

  return (
    <div className="min-h-screen flex flex-col" style={backgroundStyle}>
      <div className="flex-1 container max-w-lg mx-auto py-8 px-4">
        <ContestHeader
          contest={contest}
          totalEntries={entry.totalEntries}
          contestTotalEntries={data.totalEntries ?? null}
          showEnteredHeading={!!contest.initial_form_counts_as_entry}
        />

        {/* Contest ended state */}
        {isManuallyEnded ? (
          <div className="fade-in bg-card rounded-xl border border-border p-8 shadow-lg text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <StopCircle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-bold text-xl mb-2">This contest has ended</h3>
            <p className="text-muted-foreground">Entries are no longer being accepted.</p>
          </div>
        ) : !entry.email ? (
          <div ref={emailFormRef}>
            <EmailCollector
              contestId={contest.id}
              onSubmit={handleEmailSubmit}
              isLoading={entry.isLoadingParticipant}
              themeColors={{
                buttonBackground: theme?.buttonBackground,
                buttonText: theme?.buttonText,
              }}
              requireTermsAcceptance={contest.require_terms_acceptance}
              termsVariables={termsVariables}
              referralBonusEntries={contest.referral_bonus_entries}
              endsAt={contest.ends_at}
              startsAt={contest.starts_at}
              useCustomTerms={contest.use_custom_terms}
              customTermsText={contest.custom_terms_text}
              entryFields={contest.entry_fields}
              legalPrivacyUrl={contest.legal_privacy_url}
              legalTermsUrl={contest.legal_terms_url}
              consentHelper={contest.email_consent_helper || contest.org_email_consent_helper || null}
            />
          </div>
        ) : (
          <>
            <div className="space-y-6">
              <div className="space-y-3" ref={entryMethodsRef}>
                {entry.isLoadingParticipant ? (
                  <div className="text-center py-8 fade-in">
                    <Loader2 className="w-8 h-8 text-primary mx-auto mb-3 animate-spin" />
                    <p className="text-muted-foreground">Loading your entry status...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center min-h-[0px] empty:min-h-0">
                      <TurnstileWidget
                        ref={turnstileRef}
                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                        appearance="interaction-only"
                        onToken={handleTokenReceived}
                        onError={() => {
                          // Allow widget internal retry handling
                        }}
                        onExpire={() => {
                          setTurnstileToken(null);
                        }}
                      />
                    </div>

                    {entryMethods.filter(method => method.key !== 'signup').length === 0 ? (
                      <div className="text-center py-8 fade-in">
                        <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No entry methods available yet.</p>
                      </div>
                    ) : (
                      <>
                        {contest.show_entry_methods_heading && (
                          <h2 className="font-heading font-bold text-lg">
                            {contest.entry_methods_heading?.trim() || DEFAULT_ENTRY_METHODS_HEADING}
                          </h2>
                        )}
                        {entryMethods.filter(method => method.key !== 'signup').map((method, index) => (
                          <div
                            key={method.id}
                            className="fade-in"
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <EntryButton
                              label={method.label}
                              description={method.description}
                              entriesAwarded={method.entries_awarded}
                              actionUrl={method.action_url}
                              iconType={method.icon_type}
                              iconKey={method.icon_key}
                              iconUrl={method.icon_url}
                              isCompleted={entry.isMethodCompleted(method.id)}
                              onSubmit={() => handleEntry(method.key, method.id)}
                              disabled={isEntryDisabled || entry.isLoading || isWaitingForToken}
                              themeColors={{
                                buttonBackground: theme?.buttonBackground,
                                buttonText: theme?.buttonText,
                              }}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>

              {!isManuallyEnded && isExpired && (
                <div className="text-center py-4 px-6 bg-warning/10 rounded-xl fade-in">
                  <p className="text-warning font-medium">This contest has expired.</p>
                </div>
              )}

              {!hasStarted && (
                <div className="text-center py-4 px-6 bg-accent/10 rounded-xl fade-in">
                  <p className="text-accent font-medium">This contest hasn't started yet.</p>
                </div>
              )}

              {entry.referralCode && !isManuallyEnded && !isExpired && (
                <ShareSection
                  referralCode={entry.referralCode}
                  contestSlug={contest.slug}
                  referralBonusEntries={contest.referral_bonus_entries}
                />
              )}

              {termsVariables.winnerNotificationMethod && (
                <WinnerNotificationInfo termsVariables={termsVariables} />
              )}

              {entry.lastError && (
                <div className="text-center py-3 px-4 bg-destructive/10 rounded-lg fade-in">
                  <p className="text-destructive text-sm">{entry.lastError}</p>
                </div>
              )}
            </div>
          </>
        )}

        <ContestFooter
          termsVariables={termsVariables}
          referralBonusEntries={contest.referral_bonus_entries}
          endsAt={contest.ends_at}
          startsAt={contest.starts_at}
          planKey={planKey}
          hidePoweredByBranding={contest.hide_powered_by_branding}
          useCustomTerms={contest.use_custom_terms}
          customTermsText={contest.custom_terms_text}
          showHelpEmail={contest.show_help_email}
          helpEmail={contest.help_email}
        />

        {/* Mobile Sticky CTA Bar */}
        {!isManuallyEnded && !isExpired && hasStarted && (
          <div
            className={`fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-border px-4 py-3 md:hidden transition-all duration-300 ${showStickyBar ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full pointer-events-none"
              } pb-[max(0.75rem,env(safe-area-inset-bottom))]`}
          >
            <button
              onClick={() => {
                if (!entry.email) {
                  emailFormRef.current?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  entryMethodsRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="w-full inline-flex items-center justify-center rounded-md font-medium px-4 py-3 text-lg transition-colors hover:opacity-90 active:scale-[0.98]"
              style={{
                backgroundColor: theme?.buttonBackground || '#f97316',
                color: theme?.buttonText || '#ffffff',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              {!entry.email ? "Enter to Win →" : "View Ways to Enter →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}